import { vars, Card } from '@etg/wings';
import { css } from '@eti/styles';
import type {
  BrandedFareInfo,
  IncludedCabinBaggage,
  IncludedCheckedBaggage,
  RegionNew,
} from '@eti/schema-types';
import { TripDetailsWrapper, boundMargins } from './ItineraryComponents';
import LocationAndTime from './ItineraryLocationTime';
import SegmentInfo from './ItinerarySegmentInfo';
import ItineraryStayOverInfo from './ItineraryStayOverInfo';

const stayOverWrapperStyles = css`
  &::before {
    border-left: 1px dotted ${vars.colors.inputs.border};
    ${boundMargins};
    top: 0;
  }

  &::after {
    border-left: 1px dotted ${vars.colors.inputs.border};
    bottom: 0;
    ${boundMargins};
  }
`;

interface Carrier {
  code: string;
  name: string;
}

export interface ItinerarySegmentProps {
  arrivalDate: string;
  arrivalTime: string;
  brandedFareInformation: BrandedFareInfo;
  cabinClassName: string;
  departureTime: string;
  destination: RegionNew;
  duration: string;
  durationInDays: string;
  flightNumber: string;
  hasNextStop: boolean;
  includedCabinBaggage: IncludedCabinBaggage;
  includedCheckedBaggage: IncludedCheckedBaggage;
  marketingCarrier: Carrier;
  numberOfTechnicalStops: number;
  operatingCarrier: Carrier;
  operatingInformation: string;
  origin: RegionNew;
  shouldHideBaggageInfo: boolean;
  transportType: string;
  tripSegmentType: string;
}

const ItinerarySegment = ({
  arrivalDate,
  arrivalTime,
  brandedFareInformation,
  cabinClassName,
  departureTime,
  destination = {} as RegionNew,
  duration,
  durationInDays,
  flightNumber,
  hasNextStop,
  includedCabinBaggage,
  includedCheckedBaggage,
  marketingCarrier = {} as Carrier,
  numberOfTechnicalStops = 0,
  operatingCarrier = {} as Carrier,
  operatingInformation,
  origin = {} as RegionNew,
  shouldHideBaggageInfo,
  transportType,
  tripSegmentType,
}: ItinerarySegmentProps) => {
  return (
    <Card data-testid="tripDetails-tripSegment" padding={0}>
      <LocationAndTime
        airportName={origin.name}
        cityName={origin.cityName}
        isStartingPoint
        time={departureTime}
      />
      <SegmentInfo
        brandedFareInformation={brandedFareInformation}
        cabinClassName={cabinClassName}
        duration={duration}
        flightNumber={flightNumber}
        includedCabinBaggage={includedCabinBaggage}
        includedCheckedBaggage={includedCheckedBaggage}
        marketingCarrier={marketingCarrier}
        numberOfTechnicalStops={numberOfTechnicalStops}
        operatingCarrier={operatingCarrier}
        operatingInformation={operatingInformation}
        shouldHideBaggageInfo={shouldHideBaggageInfo}
        transportType={transportType}
        tripSegmentType={tripSegmentType}
      />
      {Boolean(durationInDays) && (
        <TripDetailsWrapper className={stayOverWrapperStyles}>
          <ItineraryStayOverInfo calendarDate={arrivalDate} durationInDays={durationInDays} />
        </TripDetailsWrapper>
      )}
      <LocationAndTime
        airportName={destination.name}
        cityName={destination.cityName}
        hasNextStop={hasNextStop}
        time={arrivalTime}
      />
    </Card>
  );
};

export default ItinerarySegment;
